import { useMemo, type ReactElement } from 'react';
import type { CostBucket } from '../../api/admin.js';

interface CostAreaChartProps {
  /** rows tal como las devuelve /admin/costs/daily?group_by=kind */
  rows: ReadonlyArray<CostBucket>;
  width?: number;
  height?: number;
}

const KIND_COLORS: Record<string, string> = {
  llm: 'var(--k-llm)',
  tts: 'var(--k-tts)',
  stt: 'var(--k-stt)',
  pronunciation: 'var(--k-pronunciation)',
  voice: 'var(--k-voice)',
  tool_call: 'var(--k-tool_call)',
  retrieval: 'var(--k-retrieval)',
  guardrail: 'var(--k-guardrail)',
};

function kindColor(kind: string | null): string {
  if (!kind) return 'var(--text-tertiary)';
  return KIND_COLORS[kind] ?? 'var(--text-tertiary)';
}

/**
 * Stacked area chart de costos por kind. Las rows vienen como
 * (date, bucket=kind, totalCostNano). Si rows.length === 0, renderea
 * un placeholder vacío.
 */
export function CostAreaChart({ rows, width = 600, height = 220 }: CostAreaChartProps): ReactElement {
  const { dates, series, max } = useMemo(() => groupSeries(rows), [rows]);

  if (dates.length === 0) {
    return (
      <div className="empty" style={{ height }}>
        Sin datos de costos en el rango.
      </div>
    );
  }

  const paddingX = 24;
  const paddingTop = 12;
  const paddingBottom = 24;
  const innerW = width - paddingX * 2;
  const innerH = height - paddingTop - paddingBottom;
  const stepX = dates.length > 1 ? innerW / (dates.length - 1) : innerW;

  const stackBase = new Array<number>(dates.length).fill(0);
  const stacked: Array<{ kind: string; color: string; areaPath: string; linePath: string }> = [];

  for (const s of series) {
    const top: Array<[number, number]> = [];
    const bottom: Array<[number, number]> = [];
    for (let i = 0; i < dates.length; i++) {
      const x = paddingX + i * stepX;
      const value = s.values[i] ?? 0;
      const yBottom = paddingTop + innerH - (stackBase[i]! / max) * innerH;
      stackBase[i] = stackBase[i]! + value;
      const yTop = paddingTop + innerH - (stackBase[i]! / max) * innerH;
      top.push([x, yTop]);
      bottom.push([x, yBottom]);
    }
    const topPath = top
      .map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`))
      .join(' ');
    const reverseBottom = bottom
      .slice()
      .reverse()
      .map((p) => `L${p[0]},${p[1]}`)
      .join(' ');
    stacked.push({
      kind: s.kind,
      color: kindColor(s.kind),
      areaPath: `${topPath} ${reverseBottom} Z`,
      linePath: topPath,
    });
  }

  const yTicks = niceTicks(max);

  return (
    <>
      <div className="chart-host">
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          {/* gridlines */}
          {yTicks.map((t) => {
            const y = paddingTop + innerH - (t.value / max) * innerH;
            return (
              <g key={t.value}>
                <line
                  x1={paddingX}
                  x2={width - paddingX}
                  y1={y}
                  y2={y}
                  stroke="var(--border-subtle)"
                  strokeDasharray="2 4"
                />
                <text x={6} y={y + 3} fill="var(--text-tertiary)" fontSize={9} fontFamily="var(--font-mono)">
                  {t.label}
                </text>
              </g>
            );
          })}
          {/* stacked areas (back-to-front) */}
          {stacked.map((s) => (
            <path key={`a-${s.kind}`} d={s.areaPath} fill={s.color} fillOpacity={0.45} />
          ))}
          {stacked.map((s) => (
            <path key={`l-${s.kind}`} d={s.linePath} fill="none" stroke={s.color} strokeWidth={1} />
          ))}
          {/* x labels (first / mid / last) */}
          {dates.length > 0 ? (
            <>
              <text
                x={paddingX}
                y={height - 6}
                fill="var(--text-tertiary)"
                fontSize={9}
                fontFamily="var(--font-mono)"
              >
                {formatDay(dates[0]!)}
              </text>
              <text
                x={width - paddingX}
                y={height - 6}
                fill="var(--text-tertiary)"
                fontSize={9}
                fontFamily="var(--font-mono)"
                textAnchor="end"
              >
                {formatDay(dates[dates.length - 1]!)}
              </text>
            </>
          ) : null}
        </svg>
      </div>
      <div className="chart-legend">
        {series.map((s) => (
          <span key={s.kind}>
            <span className="chart-legend-swatch" style={{ background: kindColor(s.kind) }} />
            {s.kind}
          </span>
        ))}
      </div>
    </>
  );
}

interface Series {
  kind: string;
  values: number[];
}

function groupSeries(rows: ReadonlyArray<CostBucket>): {
  dates: string[];
  series: Series[];
  max: number;
} {
  if (rows.length === 0) return { dates: [], series: [], max: 1 };
  const dateSet = new Set<string>();
  const kindMap = new Map<string, Map<string, number>>();
  for (const r of rows) {
    const date = r.date;
    dateSet.add(date);
    const kind = r.bucket ?? 'other';
    if (!kindMap.has(kind)) kindMap.set(kind, new Map());
    const val = toNumberSafe(r.totalCostNano);
    kindMap.get(kind)!.set(date, val);
  }
  const dates = Array.from(dateSet).sort();
  const series: Series[] = [];
  for (const [kind, byDate] of kindMap) {
    const values = dates.map((d) => byDate.get(d) ?? 0);
    series.push({ kind, values });
  }
  // Stack from largest kind to smallest so visualmente queda layered.
  series.sort((a, b) => sum(b.values) - sum(a.values));
  const totals = dates.map((_, i) => series.reduce((acc, s) => acc + (s.values[i] ?? 0), 0));
  const max = Math.max(1, ...totals);
  return { dates, series, max };
}

function sum(arr: number[]): number {
  let total = 0;
  for (const n of arr) total += n;
  return total;
}

function toNumberSafe(v: string | number | null | undefined): number {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  try {
    if (/^-?\d+$/.test(v)) return Number(BigInt(v));
    const parsed = Number(v);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function niceTicks(max: number): Array<{ value: number; label: string }> {
  if (max <= 0) return [];
  const ticks = [max / 2, max].map((v) => ({ value: v, label: shortNano(v) }));
  return ticks;
}

function shortNano(nano: number): string {
  if (!Number.isFinite(nano)) return '$0';
  const usd = nano / 1_000_000_000;
  if (usd >= 1000) return `$${(usd / 1000).toFixed(1)}k`;
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  return `$${usd.toFixed(4)}`;
}

function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isFinite(d.getTime())) {
    return `${d.toISOString().slice(5, 10)}`; // mm-dd
  }
  return iso.slice(0, 10);
}
